import * as React from 'react';
import { FunctionComponent } from 'react';
import propz from 'propz';
import { FormAnswersSummary } from '../FormAnswersSummary/FormAnswersSummary';
import { answersToMap } from 'Src/helpers/form/formRuntime';
import moment from 'moment';
import { MessageDefinition } from '../../types/message';
import { getLastResponseUpdatedAtText, getRespondedAtText, getResponseUserText } from 'Src/helpers/message/message';

interface Props {
  message: MessageDefinition;
}

const pad2 = (n: number) => ('0' + n).slice(-2);

const formatDateTime = (raw: any): string => {
  if (!raw) return '';
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return '';

  return `${pad2(d.getDate())}/${pad2(d.getMonth() + 1)}/${d.getFullYear()} ${pad2(d.getHours())}:${pad2(
    d.getMinutes()
  )}`;
};

const formatTournamentWindow = (startRaw: any, endRaw: any): string => {
  if (!startRaw) return '';

  const start = new Date(startRaw);
  if (Number.isNaN(start.getTime())) return '';

  const formatDate = (d: Date) => `${pad2(d.getDate())}/${pad2(d.getMonth() + 1)}/${d.getFullYear()}`;

  const formatTime = (d: Date) => `${pad2(d.getHours())}:${pad2(d.getMinutes())}`;

  if (!endRaw) {
    return `${formatDate(start)} ${formatTime(start)}`;
  }

  const end = new Date(endRaw);
  if (Number.isNaN(end.getTime())) {
    return `${formatDate(start)} ${formatTime(start)}`;
  }

  const isSameDay =
    start.getFullYear() === end.getFullYear() &&
    start.getMonth() === end.getMonth() &&
    start.getDate() === end.getDate();

  if (isSameDay) {
    return `${formatDate(start)} ${formatTime(start)} - ${formatTime(end)}`;
  }

  return `${formatDate(start)} ${formatTime(start)} - ${formatDate(end)} ${formatTime(end)}`;
};

const formatRemaining = (ms: number): string => {
  if (ms <= 0) return 'Passed';

  const totalMinutes = Math.floor(ms / 60000);
  const days = Math.floor(totalMinutes / (60 * 24));
  const hours = Math.floor((totalMinutes - days * 60 * 24) / 60);
  const minutes = totalMinutes - days * 60 * 24 - hours * 60;

  const hhmm = `${pad2(hours)}:${pad2(minutes)}`;
  return days > 0 ? `${days}d ${hhmm}` : hhmm;
};

const renderDeadlinePill = (deadlineIso: any): React.ReactNode => {
  if (!deadlineIso) return null;

  const now = moment();
  const deadline = moment(deadlineIso);
  const msLeft = deadline.diff(now);

  const oneWeekMs = 7 * 24 * 60 * 60 * 1000;
  const threeDaysMs = 3 * 24 * 60 * 60 * 1000;
  const oneDayMs = 24 * 60 * 60 * 1000;

  let style: React.CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    padding: '6px 10px',
    borderRadius: 999,
    fontSize: 12,
    fontWeight: 700,
    border: '1px solid rgba(0,0,0,0.08)',
    background: '#f3f4f6',
    color: '#6b7280'
  };

  let text = `Reply by ${deadline.format('DD/MM/YYYY HH:mm')}`;

  if (msLeft <= 0) {
    style = {
      ...style,
      background: '#f3f4f6',
      borderColor: '#e5e7eb',
      color: '#6b7280'
    };
    text = `Passed • ${deadline.format('DD/MM/YYYY HH:mm')}`;
  } else if (msLeft <= oneDayMs) {
    style = {
      ...style,
      background: '#fee2e2',
      borderColor: '#fecaca',
      color: '#991b1b'
    };
    text = `Today • ${deadline.format('DD/MM/YYYY HH:mm')}`;
  } else if (msLeft <= threeDaysMs) {
    style = {
      ...style,
      background: '#ffedd5',
      borderColor: '#fed7aa',
      color: '#9a3412'
    };
    text = `Soon • ${deadline.format('DD/MM/YYYY HH:mm')}`;
  } else if (msLeft <= oneWeekMs) {
    style = {
      ...style,
      background: '#fef3c7',
      borderColor: '#fde68a',
      color: '#92400e'
    };
    text = `This week • ${deadline.format('DD/MM/YYYY HH:mm')}`;
  } else {
    style = {
      ...style,
      background: '#dcfce7',
      borderColor: '#bbf7d0',
      color: '#166534'
    };
  }

  return (
    <span style={style} title={msLeft <= 0 ? 'Deadline passed' : `Time left: ${formatRemaining(msLeft)}`}>
      {text}
    </span>
  );
};

export const TournamentConfirmationRequestSummary: FunctionComponent<Props> = ({ message }) => {
  const tournamentName = String(propz.get(message, ['tournamentData', 'name'], 'Tournament'));
  const senderName = String(propz.get(message, ['sender', 'fullName'], '') || '—');
  const createdAt = formatDateTime(propz.get(message, ['createdAt'], null));
  const startTime = propz.get(message, ['tournamentData', 'startTime'], null);
  const endTime = propz.get(message, ['tournamentData', 'endTime'], null);
  const deadline = propz.get(message, ['deadlineForAnswers'], null);
  const venuePostcode = String(propz.get(message, ['tournamentData', 'venue', 'postcode'], '') || '').trim();
  const schoolName = String(propz.get(message, ['schoolData', 'name'], '') || '').trim();
  const respondedByText = getResponseUserText(message);
  const respondedAtText = getRespondedAtText(message);
  const lastChangedAtText = getLastResponseUpdatedAtText(message);

  const windowText = formatTournamentWindow(startTime, endTime);
  const legacyFields = Array.isArray(message.fields) ? message.fields : [];
  const hasLegacyAnswers = legacyFields.some(field => typeof field.value !== 'undefined');

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div
        style={{
          padding: '14px 16px',
          border: '1px solid rgba(0,0,0,0.08)',
          borderRadius: 14,
          background: 'linear-gradient(180deg, rgba(0,0,0,0.025), rgba(0,0,0,0.01))'
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: 16,
            flexWrap: 'wrap'
          }}
        >
          <div style={{ minWidth: 0, flex: '1 1 320px' }}>
            <div
              style={{
                fontSize: 12,
                fontWeight: 700,
                letterSpacing: '0.04em',
                textTransform: 'uppercase',
                color: '#8b96a3'
              }}
            >
              Tournament confirmation request
            </div>

            <div
              style={{
                marginTop: 4,
                fontSize: 20,
                fontWeight: 800,
                lineHeight: 1.2,
                color: '#1f2d3d'
              }}
            >
              {tournamentName}
            </div>
          </div>

          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
            {deadline ? renderDeadlinePill(deadline) : null}

            {schoolName ? (
              <span
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  padding: '6px 10px',
                  borderRadius: 999,
                  fontSize: 12,
                  fontWeight: 700,
                  background: '#f8fafc',
                  border: '1px solid rgba(0,0,0,0.08)',
                  color: '#475569'
                }}
              >
                {schoolName}
              </span>
            ) : null}
          </div>
        </div>
      </div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr',
          gap: 12
        }}
      >
        <div
          style={{
            padding: '12px 14px',
            border: '1px solid rgba(0,0,0,0.07)',
            borderRadius: 12,
            background: '#fff'
          }}
        >
          <div
            style={{
              fontSize: 11,
              fontWeight: 700,
              lineHeight: 1.2,
              textTransform: 'uppercase',
              letterSpacing: '0.04em',
              color: '#8b96a3'
            }}
          >
            Tournament window
          </div>
          <div style={{ marginTop: 8, fontSize: 16, lineHeight: 1.45, color: '#233142' }}>{windowText || '—'}</div>
        </div>

        <div
          style={{
            padding: '12px 14px',
            border: '1px solid rgba(0,0,0,0.07)',
            borderRadius: 12,
            background: '#fff'
          }}
        >
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 10
            }}
          >
            <span
              style={{
                display: 'flex',
                alignItems: 'baseline',
                gap: 6,
                minWidth: 0,
                padding: '6px 10px',
                borderRadius: 10,
                background: 'rgba(0,0,0,0.025)',
                border: '1px solid rgba(0,0,0,0.06)'
              }}
            >
              <span
                style={{
                  fontSize: 12,
                  fontWeight: 700,
                  color: '#7b8794',
                  textTransform: 'uppercase',
                  letterSpacing: '0.03em'
                }}
              >
                Created
              </span>
              <span style={{ fontSize: 13, color: '#1f2d3d' }}>{createdAt || '—'}</span>
            </span>

            <span
              style={{
                display: 'flex',
                alignItems: 'baseline',
                gap: 6,
                minWidth: 0,
                padding: '6px 10px',
                borderRadius: 10,
                background: 'rgba(0,0,0,0.025)',
                border: '1px solid rgba(0,0,0,0.06)'
              }}
            >
              <span
                style={{
                  fontSize: 12,
                  fontWeight: 700,
                  color: '#7b8794',
                  textTransform: 'uppercase',
                  letterSpacing: '0.03em'
                }}
              >
                Sender
              </span>
              <span style={{ fontSize: 13, color: '#1f2d3d' }}>{senderName}</span>
            </span>

            {respondedByText ? (
              <span
                style={{
                  display: 'flex',
                  alignItems: 'baseline',
                  gap: 6,
                  minWidth: 0,
                  padding: '6px 10px',
                  borderRadius: 10,
                  background: 'rgba(0,0,0,0.025)',
                  border: '1px solid rgba(0,0,0,0.06)',
                  whiteSpace: 'pre-line'
                }}
              >
                <span
                  style={{
                    fontSize: 12,
                    fontWeight: 700,
                    color: '#7b8794',
                    textTransform: 'uppercase',
                    letterSpacing: '0.03em'
                  }}
                >
                  Responded by
                </span>
                <span style={{ fontSize: 13, color: '#1f2d3d' }}>{respondedByText}</span>
              </span>
            ) : null}

            {respondedAtText ? (
              <span
                style={{
                  display: 'flex',
                  alignItems: 'baseline',
                  gap: 6,
                  minWidth: 0,
                  padding: '6px 10px',
                  borderRadius: 10,
                  background: 'rgba(0,0,0,0.025)',
                  border: '1px solid rgba(0,0,0,0.06)'
                }}
              >
                <span
                  style={{
                    fontSize: 12,
                    fontWeight: 700,
                    color: '#7b8794',
                    textTransform: 'uppercase',
                    letterSpacing: '0.03em'
                  }}
                >
                  Responded at
                </span>
                <span style={{ fontSize: 13, color: '#1f2d3d' }}>{respondedAtText}</span>
              </span>
            ) : null}
            {lastChangedAtText ? (
              <span
                style={{
                  display: 'flex',
                  alignItems: 'baseline',
                  gap: 6,
                  minWidth: 0,
                  padding: '6px 10px',
                  borderRadius: 10,
                  background: 'rgba(0,0,0,0.025)',
                  border: '1px solid rgba(0,0,0,0.06)'
                }}
              >
                <span
                  style={{
                    fontSize: 12,
                    fontWeight: 700,
                    color: '#7b8794',
                    textTransform: 'uppercase',
                    letterSpacing: '0.03em'
                  }}
                >
                  Last changed
                </span>
                <span style={{ fontSize: 13, color: '#1f2d3d' }}>{lastChangedAtText}</span>
              </span>
            ) : null}
            {venuePostcode ? (
              <span
                style={{
                  display: 'flex',
                  alignItems: 'baseline',
                  gap: 6,
                  minWidth: 0,
                  padding: '6px 10px',
                  borderRadius: 10,
                  background: 'rgba(0,0,0,0.025)',
                  border: '1px solid rgba(0,0,0,0.06)'
                }}
              >
                <span
                  style={{
                    fontSize: 12,
                    fontWeight: 700,
                    color: '#7b8794',
                    textTransform: 'uppercase',
                    letterSpacing: '0.03em'
                  }}
                >
                  Venue
                </span>
                <span style={{ fontSize: 13, color: '#1f2d3d' }}>{venuePostcode}</span>
              </span>
            ) : null}
          </div>
        </div>
      </div>

      {message.formDefinition ? (
        <div>
          <div style={{ fontWeight: 700, marginBottom: 8 }}>Submitted answers</div>
          <FormAnswersSummary
              definition={message.formDefinition}
              answers={answersToMap(message.formAnswers || [])}
            />
        </div>
      ) : hasLegacyAnswers ? (
        <div>
          <div style={{ fontWeight: 700, marginBottom: 8 }}>Submitted answers</div>
          {legacyFields.map((field, index) => (
            <div key={String(field._id || index)} style={{ padding: '8px 0', borderBottom: '1px solid #eee' }}>
              <div style={{ fontWeight: 600 }}>{field.heading || 'Question'}</div>
              <div>
                {field.type === 'BOOLEAN'
                  ? String(field.value) === 'true'
                    ? 'Yes'
                    : 'No'
                  : Array.isArray(field.value)
                  ? field.value.join(', ')
                  : String(field.value ?? '—')}
              </div>
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
};

TournamentConfirmationRequestSummary.displayName = 'TournamentConfirmationRequestSummary';



